import React, { useEffect, useState, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { Row, Col, CardBody, Card, Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import DataTable from 'react-data-table-component';
import DiscountCouponService from "../DiscountCoupon/Discountcoupons.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import { Tooltip } from "@mui/material";
import PharmacyLadgerDetailsFilter from "../../components/Table Filters/PharmacyLadgerDetailsFilter.js";
import PermissionAccess from '../../PermissionAccess/Access.js';
import { openStoredFile } from "../../utils/fileUtils";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { downloadLedgerPdf } from "../../utils/ledgerPdfGenerator";

// Custom styles for the ledger detail modal
const modalStyles = `
  .ledger-detail-modal .modal-content {
    border: none;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  }
  
  .ledger-detail-modal .modal-header {
    border-radius: 15px 15px 0 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  }
  
  .ledger-detail-modal .card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border-radius: 10px;
  }
  
  .ledger-detail-modal .card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }
  
  .ledger-detail-modal .card-header {
    border-radius: 10px 10px 0 0;
    font-weight: 600;
    padding: 0.5rem 0.75rem;
  }
  
  .ledger-detail-modal .card-body {
    padding: 0.5rem 0.75rem;
  }
  
  .ledger-detail-modal .info-item {
    margin-bottom: 0.5rem;
  }
  
  .ledger-detail-modal .info-item:last-child {
    margin-bottom: 0;
  }
  
  .ledger-detail-modal .info-item label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 0.2rem;
  }
  
  .ledger-detail-modal .info-item .fw-bold {
    font-size: 0.85rem;
  }
  
  .ledger-detail-modal .party-card {
    border: 1px solid #e9ecef;
    transition: all 0.2s ease;
  }
  
  .ledger-detail-modal .party-card:hover {
    border-color: #007bff;
    background-color: #f8f9fa !important;
  }
  
  .ledger-detail-modal .badge {
    font-weight: 500;
    letter-spacing: 0.5px;
  }
  
  .ledger-detail-modal .modal-footer {
    border-radius: 0 0 15px 15px;
  }
`;

const PharmacyLadgerDetailsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [pharmacyLedgerDetails, setPharmacyLedgerDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);

  const { state } = useLocation();
  const pharmacyLadgerId = state?.pharmacy_ladger_id;
  const pharmacyName = state?.pharmacy_name;
  const pharmacyTotal = state?.pharmacy_total_amount ?? 0; // fallback to 0

  // New state for ledger detail modal
  const [showLedgerDetailModal, setShowLedgerDetailModal] = useState(false);
  const [selectedLedgerDetail, setSelectedLedgerDetail] = useState(null);
  const [ledgerDetailLoading, setLedgerDetailLoading] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const [pageLimit, setPageLimit] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const { authData, accessPages, super_admin, current_module } = PermissionAccess();

  const [showModalAddDiscountCoupon, setShowModalAddDiscountCoupon] = useState(false);
  const handleToggleModalAddDiscountCoupon = () => {
    setShowModalAddDiscountCoupon(!showModalAddDiscountCoupon);
  }

  const [showModalEditDiscountCoupon, setShowModalEditDiscountCoupon] = useState(false);
  const handleToggleModalEditDiscountCoupon = (id) => {
    setShowModalEditDiscountCoupon(!showModalEditDiscountCoupon);
    setSelectedId(id); // Set the selected ID when toggling modal
  };

  // Function to handle viewing ledger details
  const handleViewLedgerDetail = async (ledgerId) => {
    setLedgerDetailLoading(true);
    setShowLedgerDetailModal(true);
    
    try {
      const response = await DiscountCouponService.getSinglePharmacyLadgerDetails(
        authData.sessionID,
        ledgerId
      );
      
      if (response.data.status) {
        setSelectedLedgerDetail(response.data.data);
      } else {
        Alerts.swalErrorAlert(response.data.message || "Failed to fetch ledger details");
      }
    } catch (error) {
      const errContent = (error.response && error.response.data) || error.message || error.toString();
      Alerts.swalErrorAlert(errContent || "Failed to fetch ledger details");
    } finally {
      setLedgerDetailLoading(false);
    }
  };

  // Function to close ledger detail modal
  const handleCloseLedgerDetailModal = () => {
    setShowLedgerDetailModal(false);
    setSelectedLedgerDetail(null);
  };

  // Calculate totals excluding rejected transactions
  const calculateTotals = () => {
    const credit = pharmacyLedgerDetails
      .filter(row => row.ladger_action === '+')
      .reduce((sum, row) => sum + (row.pharmacy_actual_amount || 0), 0);
    
    // Only count actual debit transactions, exclude rejected withdrawal requests
    const debit = pharmacyLedgerDetails
      .filter(row => row.ladger_action === '-' && row.status !== 'Rejected')
      .reduce((sum, row) => sum + (row.pharmacy_actual_amount || 0), 0);
    
    // Calculate total amount (sum of all amounts excluding pending transactions)
    const totalAmount = pharmacyLedgerDetails
      .filter(row => row.ladger_action && row.ladger_action !== '') // Exclude pending transactions
      .reduce((sum, row) => sum + (row.pharmacy_actual_amount || 0), 0);
    
    return { credit, debit, balance: credit - debit, totalAmount };
  };

  const totals = calculateTotals();

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('language'));
    fetchPharmacyLedgerDetails(0, pageLimit);
  }, [pageLimit]);


  function fetchPharmacyLedgerDetails(page_index, page_limit, status) {
    setLoading(true);
    var offset = (page_index - 1) * page_limit;
    if (offset < 1) {
      offset = 0;
    }

    
    DiscountCouponService.getPharmacyLadgerDetails(authData.sessionID,offset, page_limit, status, pharmacyLadgerId).then(
      (response) => {
        setLoading(false);
        console.log("Get pharmacy ledger details response:-", response);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          setPharmacyLedgerDetails(response.data.data);
          setTotalRows(response.data.count.count);
        }
      },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        if (errContent) {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      }
    );
  }



  const mycolumns = [
   {
    name: t('Order Date'),
    selector: row =>
      moment(row.datetime_added).format("DD MMMM YYYY, hh:mm A"),
    width: "20%",
    sortable: true,
  },

   {
      name: t('Patient'),
      selector: row => row.patient_details?.full_name || "-",
      width: "20%",
      sortable: true,
  },

    {
      name: t('Activity'),
      selector: "required_action",
      width: "40%",
      sortable: true,
    },
    

{
  name: t('Amount'),
  cell: row => {
    // Handle rejected transactions
    if (row.ladger_action === "-" && row.status === "Rejected") {
      return (
        <span className="badge bg-secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
          {t("Rejected")}
        </span>
      );
    }
    
    // Handle pending transactions (empty ladger_action)
    if (!row.ladger_action || row.ladger_action === "") {
      return (
        <span className="badge bg-warning" style={{ fontSize: '12px', color: '#000' }}>
        RM {row.pharmacy_actual_amount?.toFixed(2) || "0.00"}
        </span>
      );
    }
    
    // Handle normal transactions
    const isNegative = row.ladger_action === "-";
    const badgeClass = isNegative ? "bg-danger" : "bg-success";

    return (
      <span className={`badge ${badgeClass}`} style={{ fontSize: '12px' }}>
        {row.ladger_action} RM {row.pharmacy_actual_amount?.toFixed(2) || "0.00"}
      </span>
    );
  },
  width: "12%",
  sortable: true,
},



{
  name: t('Action'),
  cell: row => {
    return (
      <div className="d-flex gap-2">
        {/* Eye icon for viewing ledger details */}
        <Tooltip title={t("View Details")} arrow>
          <i
            className="pe-7s-look text-info fs-5"
            style={{ cursor: "pointer" }}
            onClick={() => handleViewLedgerDetail(row._id)}
          ></i>
        </Tooltip>
        
        {/* PDF icon if file exists */}
        {row.atteched_file && (
          <Tooltip title={t("View Attached PDF")} arrow>
            <i
              className="pe-7s-news-paper text-primary fs-5"
              style={{ cursor: "pointer" }}
              onClick={() => openStoredFile(row.atteched_file, 'pdf')}
            ></i>
          </Tooltip>
        )}
      </div>
    );
  },
  width: "8%",
  sortable: false,
},

  ];



  const handleSearch = (status) => {
    setFilterStatus(status);
    if (status !== '') {
      fetchPharmacyLedgerDetails(0, pageLimit, status)
    }
  };

  const handleClear = () => {
    setFilterStatus('');
    fetchPharmacyLedgerDetails(0, pageLimit);
  };
  const filteredItems = pharmacyLedgerDetails;


 

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <PharmacyLadgerDetailsFilter
        onSearch={handleSearch}
        onClear={handleClear}
        filterStatus={filterStatus}
      />
    );
  }, [filterStatus]);


  function handlePageChange(page_index) {
    console.log("Page index:-", page_index)
    setCurrentPage(page_index)
    fetchPharmacyLedgerDetails(page_index, pageLimit, filterStatus);
  }

  async function handlePerRowsChange(newPageLimit, page_index) {
    setPageLimit(newPageLimit);
  }

  const handleDownloadPdf = async () => {
    setPdfDownloading(true);
    try {
      const response = await DiscountCouponService.getPharmacyLadgerDetails(
        authData.sessionID,
        0,
        10000,
        filterStatus,
        pharmacyLadgerId
      );

      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
        return;
      }

      const transactions = response.data.data || [];
      if (!transactions.length) {
        Alerts.swalErrorAlert(t("No transaction data available."));
        return;
      }

      await downloadLedgerPdf({
        data: transactions,
        entityName: pharmacyName || t("Pharmacy"),
        ledgerType: "pharmacy",
        fileName: `${(pharmacyName || "Pharmacy").replace(/[^a-zA-Z0-9_-]/g, "_")}_Ledger_${moment().format("YYYY-MM-DD")}.pdf`,
        t,
      });
      Alerts.swalSuccessAlert(t("PDF generated successfully"));
    } catch (error) {
      Alerts.swalErrorAlert(error.message || t("Failed to generate PDF. Please try again."));
    } finally {
      setPdfDownloading(false);
    }
  };

  return (
    <Fragment>
      <style>{modalStyles}</style>

      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="bg-light-gray bg-animation">
            <div className="d-flex justify-content-center align-items-center">
              <Col md="12" className="mx-auto app-login-box">
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-news-paper icon-gradient bg-tempting-azure" />
                      </div>
                     <div>
                      {`${pharmacyName} ${t("Ledger")}`}
                      <div className="page-title-subheading">
                        {`${t("Details for")} ${pharmacyName} ${t("Ledger")}` } 
                      </div>
                    </div>
                    </div>
                    <div className="page-title-actions">
                      <button
                        type="button"
                        className="btn btn-outline-success btn-hover-shine me-3"
                        onClick={handleDownloadPdf}
                        disabled={pdfDownloading}
                      >
                        <i className="pe-7s-download me-2" />
                        {pdfDownloading ? t("Generating PDF...") : t("Download PDF")}
                      </button>
                      <button type="button" onClick={() => navigate(-1)} className="btn btn-outline-success btn-hover-shine me-3">
                        {t("Back")}
                      </button>
                    </div>
                  </div>
                </div>
                <Row>
                  <LoadingOverlay
                    tag="div"
                    active={loading}
                    styles={{ overlay: (base) => ({ ...base }) }}
                    spinner={<DNALoader />}
                  >
                    <Col md="12">
                      <Card className="main-card mb-3">
                        <CardBody>
                          <Col md="6" style={{ float: "left", position: "absolute", zIndex: "1" }}>
                            {subHeaderComponentMemo}
                          </Col>

                          <DataTable
  data={filteredItems}
  columns={mycolumns}
  pagination
  fixedHeader
  fixedHeaderScrollHeight="400px"
  subHeader
  subHeaderComponent={
    <div style={{ width: '100%', paddingTop: '60px' }}> {/* Add padding-top to create space for the filter */}
      <div
        className="d-flex flex-wrap align-items-center gap-2"
        style={{ margin: '10px 0 16px 0' }}
      >
        <span
          style={{
            color: '#222',
            fontWeight: 500,
            fontSize: 15,
            marginRight: 18,
            letterSpacing: 0.2,
          }}
        >
          {t('Total Unpaid Amount')}: <span style={{ color: '#1bd35a', fontWeight: 700 }}>RM {(pharmacyTotal || 0).toFixed(2)}</span>
        </span>

      </div>
    </div>
  }
  paginationServer
  paginationTotalRows={totalRows}
  onChangePage={handlePageChange}
  onChangeRowsPerPage={handlePerRowsChange}
  paginationResetDefaultPage={resetPaginationToggle}
/>
                          
                        </CardBody>
                      </Card>
                    </Col>
                  </LoadingOverlay>
                </Row>
              </Col>
            </div>
          </div>
        </div>
      </div>

      {/* Ledger Detail Modal */}
      <Modal isOpen={showLedgerDetailModal} toggle={handleCloseLedgerDetailModal} size="lg" className="ledger-detail-modal">
        <ModalHeader toggle={handleCloseLedgerDetailModal} className="bg-gradient-primary text-white">
          <div className="d-flex align-items-center">
            <i className="pe-7s-news-paper me-3 fs-4"></i>
            <div>
              <h5 className="mb-0">{t("Ledger Detail")}</h5>
              <small className="text-light">{selectedLedgerDetail?.required_action || ""}</small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="p-4">
          {ledgerDetailLoading ? (
            <div className="text-center p-5">
              <DNALoader />
              <p className="mt-3 text-muted">{t("Loading ledger details...")}</p>
            </div>
          ) : selectedLedgerDetail ? (
            <div className="row g-4">
              {/* Transaction Information Card */}
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-primary text-white">
                    <h6 className="mb-0">
                      <i className="pe-7s-clock me-2"></i>
                      {t("Transaction Information")}
                    </h6>
                  </div>
                  <div className="card-body py-3">
                    
                    <div className="info-item mb-2">
                      <label className="text-muted small mb-1">{t("Date & Time")}</label>
                      <div className="fw-bold text-dark">
                        <i className="pe-7s-date me-2 text-primary"></i>
                        {moment(selectedLedgerDetail.datetime_added).format("DD MMMM YYYY, hh:mm A")}
                      </div>
                    </div>
                    <div className="info-item mb-2">
                      <label className="text-muted small mb-1">{t("Action")}</label>
                      <div>
                        <span className={`badge ${selectedLedgerDetail.ladger_action === '+' ? 'bg-success' : 'bg-danger'} fs-6 px-3 py-2`}>
                          <i className={`pe-7s-${selectedLedgerDetail.ladger_action === '+' ? 'plus' : 'minus'} me-2`}></i>
                          {selectedLedgerDetail.ladger_action} RM {selectedLedgerDetail.pharmacy_actual_amount?.toFixed(2) || "0.00"}
                        </span>
                      </div>
                    </div>
                    
                    <div className="info-item">
                      <label className="text-muted small mb-1">{t("Required Action")}</label>
                      <div className="fw-bold text-dark">{selectedLedgerDetail.required_action}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Financial Details Card */}
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white">
                    <h6 className="mb-0">
                      <i className="pe-7s-cash me-2"></i>
                      {t("Financial Details")}
                    </h6>
                  </div>
                  <div className="card-body py-3">
                    <div className="info-item mb-2">
                      <label className="text-muted small mb-1">{t("Total Amount")}</label>
                      <div className="fw-bold text-success fs-5">
                        RM {selectedLedgerDetail.total_amount?.toFixed(2) || "0.00"}
                      </div>
                    </div>
                    <div className="info-item mb-2">
                      <label className="text-muted small mb-1">{t("Sara Share")}</label>
                      <div className="fw-bold text-info fs-6">
                        RM {selectedLedgerDetail.sara_share?.toFixed(2) || "0.00"}
                      </div>
                    </div>
                    <div className="info-item">
                      <label className="text-muted small mb-1">{t("Pharmacy Amount")}</label>
                      <div className="fw-bold text-primary fs-6">
                        RM {selectedLedgerDetail.pharmacy_actual_amount?.toFixed(2) || "0.00"}
                      </div>
                    </div>
                    {selectedLedgerDetail.shipping_charges && (
                      <div className="info-item">
                        <label className="text-muted small mb-1">{t("Shipping Charges")}</label>
                        <div className="fw-bold text-warning fs-6">
                          RM {selectedLedgerDetail.shipping_charges?.toFixed(2) || "0.00"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              
              
              {/* Party Information Card */}
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-warning text-dark">
                    <h6 className="mb-0">
                      <i className="pe-7s-users me-2"></i>
                      {t("Party Information")}
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="party-card bg-light p-3 rounded">
                          <h6 className="text-success mb-3">
                            <i className="pe-7s-shop me-2"></i>
                            {t("Pharmacy Details")}
                          </h6>
                          <div className="info-item mb-2">
                            <label className="text-muted small mb-1">{t("Name")}</label>
                            <div className="fw-bold text-dark">{selectedLedgerDetail.pharmacy_details?.merchantName || selectedLedgerDetail.pharmacy_details?.contactPerson || selectedLedgerDetail.pharmacy_details?.companyName || "-"}</div>
                          </div>
                          <div className="info-item mb-2">
                            <label className="text-muted small mb-1">{t("Email")}</label>
                            <div className="fw-bold text-dark">
                              <i className="pe-7s-mail me-2 text-success"></i>
                              {selectedLedgerDetail.pharmacy_details?.contactEmail || "-"}
                            </div>
                          </div>
                          <div className="info-item">
                            <label className="text-muted small mb-1">{t("Phone")}</label>
                            <div className="fw-bold text-dark">
                              <i className="pe-7s-phone me-2 text-success"></i>
                              {selectedLedgerDetail.pharmacy_details?.contactPhone || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="party-card bg-light p-3 rounded">
                          <h6 className="text-info mb-3">
                            <i className="pe-7s-user me-2"></i>
                            {t("Patient Details")}
                          </h6>
                          <div className="info-item mb-2">
                            <label className="text-muted small mb-1">{t("Name")}</label>
                            <div className="fw-bold text-dark">{selectedLedgerDetail.patient_details?.full_name || "-"}</div>
                          </div>
                          <div className="info-item mb-2">
                            <label className="text-muted small mb-1">{t("Email")}</label>
                            <div className="fw-bold text-dark">
                              <i className="pe-7s-mail me-2 text-info"></i>
                              {selectedLedgerDetail.patient_details?.email || "-"}
                            </div>
                          </div>
                          <div className="info-item">
                            <label className="text-muted small mb-1">{t("Phone")}</label>
                            <div className="fw-bold text-dark">
                              <i className="pe-7s-phone me-2 text-info"></i>
                              {selectedLedgerDetail.patient_details?.phone || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center p-5">
              <i className="pe-7s-close-circle text-muted" style={{ fontSize: '4rem' }}></i>
              <p className="text-muted mt-3">{t("No ledger details found")}</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="bg-light">
          <Button color="secondary" onClick={handleCloseLedgerDetailModal} className="px-4">
            <i className="pe-7s-close me-2"></i>
            {t("Close")}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default PharmacyLadgerDetailsList;
